import { formatDistanceToNow } from 'date-fns'
import { enUS, vi } from 'date-fns/locale'
import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'

import { HiringWithCompany } from '@/pages/jobs/[slug]'

import { Badge } from '../ui/badge'

import { CompanyType } from '@/types/hiring.type'
import { UserProfile } from '@/types/user.type'

const HiringCardHeader = ({ company }: { company: CompanyType | null }) => (
  <div className="relative bg-gray-50 rounded-t-lg">
    <div className="flex items-start gap-3 sm:gap-4 mb-3 p-3 sm:p-4 relative">
      {company?.logo ? (
        <div className="w-16 h-16 sm:w-20 sm:h-20 relative flex-shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 p-1">
          <Image src={company.logo} alt={`${company.name} logo`} fill className="rounded-lg object-contain p-1" />
        </div>
      ) : (
        <div className="w-16 h-16 sm:w-20 sm:h-20 relative flex-shrink-0 bg-blue-50 rounded-lg shadow-sm border border-gray-200 flex items-center justify-center">
          <span className="text-2xl font-bold text-blue-600">{company?.name?.charAt(0) || 'C'}</span>
        </div>
      )}
      <div className="flex-1 min-w-0 pt-2">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate group-hover:text-blue-600">
          {company?.name}
        </h2>
        {company?.domain && <p className="text-xs sm:text-sm text-gray-600 truncate">{company.domain}</p>}
      </div>
    </div>
  </div>
)

type HiringCardBodyProps = {
  job: HiringWithCompany
  user: UserProfile | undefined
}

const HiringCardBody = ({ job, user }: HiringCardBodyProps) => (
  <>
    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 leading-snug px-1 sm:px-2">{job.title}</h3>

    <div className="flex-1 space-y-3 sm:space-y-4 text-xs sm:text-sm text-gray-600 px-1 sm:px-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
        <div className="flex items-center gap-2 p-2 rounded-lg border border-blue-100 shadow-sm">
          <div className="text-blue-600">
            <LocationIcon />
          </div>
          <span className="font-medium truncate">{job.nation || 'Remote'}</span>
        </div>
        <div className="flex items-center gap-2 p-2 rounded-lg border border-indigo-100 shadow-sm">
          <div className="text-indigo-600">
            <ExperienceIcon />
          </div>
          <span className="font-medium truncate">{job.yearOfExp || 'N/A'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
        <div className="flex items-center gap-2 p-2 rounded-lg border border-green-100 shadow-sm">
          <div className="text-green-600">
            <VacancyIcon />
          </div>
          <span className="font-medium truncate">{job.numberOfVacancies || 'N/A'} positions</span>
        </div>
        <div className="flex items-center gap-2 p-2 rounded-lg border border-purple-100 shadow-sm">
          <div className="text-purple-600">
            <SalaryIcon />
          </div>
          {user ? (
            <span className="font-medium truncate">
              {job.minSalary && job.maxSalary
                ? `${job.minSalary} - ${job.maxSalary} (${job.salaryCurrency})`
                : job.maxSalary
                  ? `Upto ${job.maxSalary} (${job.salaryCurrency})`
                  : job.minSalary
                    ? `Starting from ${job.minSalary} (${job.salaryCurrency})`
                    : 'Negotiate'}
            </span>
          ) : (
            <Link href="/sign-in" className="text-blue-600 hover:underline text-xs sm:text-sm truncate">
              Login to see salary
            </Link>
          )}
        </div>
      </div>
    </div>

    {job.company && (job.company.type || job.company.size) && (
      <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-100 px-3 sm:px-6">
        <div className="flex flex-wrap gap-2">
          {job.company.type && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 uppercase">
              {job.company.type}
            </Badge>
          )}
          {job.company.size && (
            <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
              {job.company.size} employees
            </Badge>
          )}
        </div>
      </div>
    )}
  </>
)

type HiringCardFooterProps = {
  createdAt: string
  lang: string
  isJobOwner: boolean | undefined
}

const HiringCardFooter = ({ createdAt, lang, isJobOwner }: HiringCardFooterProps) => (
  <div className="mt-4 mb-1 sm:mb-2 text-xs sm:text-sm text-gray-500 px-3 sm:px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
    <div className="flex items-center gap-1">
      <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span>
      <span className="whitespace-nowrap">
        Posted:{' '}
        {formatDistanceToNow(new Date(createdAt), {
          addSuffix: true,
          locale: lang === 'vi' ? vi : enUS,
        })}
      </span>
    </div>
    <Badge
      className={`self-end sm:self-auto text-white shadow-sm cursor-pointer ${
        isJobOwner ? 'bg-blue-600 hover:bg-blue-500' : 'bg-red-600 hover:bg-red-500'
      }`}
    >
      {isJobOwner ? 'View Your Post' : 'Apply Now'}
    </Badge>
  </div>
)

const LocationIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

const ExperienceIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
)

const VacancyIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
  </svg>
)

const SalaryIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

type HiringCardProps = {
  job: HiringWithCompany
  user: UserProfile | undefined
  lang: string
}

export const HiringCard = ({ job, user, lang }: HiringCardProps) => {
  const isJobOwner = user && user.id === job.user?.id

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }} className="h-full">
      <Link
        href={`/jobs/${job.slug}`}
        className="group flex flex-col h-full bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-200 hover:border-blue-300 overflow-hidden hover:shadow-blue-100 transition-all duration-300 ease-in-out"
      >
        <div className="flex flex-col h-full">
          <HiringCardHeader company={job.company} />
          <div className="relative flex flex-col h-full p-3 sm:p-5">
            <HiringCardBody job={job} user={user} />
            <HiringCardFooter createdAt={job.createdAt} lang={lang} isJobOwner={isJobOwner} />
          </div>
        </div>
      </Link>
    </motion.div>
  )
}
